import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAssessment } from '../context/AssessmentContext';
import { useAuth } from '../context/AuthContext';
import { assessmentApi } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import {
  Building2,
  Rocket,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Calculator,
  Banknote,
  Loader2,
  ClipboardList
} from 'lucide-react';

const LOAN_AMOUNTS = [
  { value: 'under_250k', label: 'Under $250,000', icon: '💵' },
  { value: '250k_to_1m', label: '$250,000 - $1,000,000', icon: '💰' },
  { value: 'above_1m', label: 'Above $1,000,000', icon: '🏦' }
];

const FUNDING_PURPOSES = [
  { value: 'working_capital', label: 'Working Capital' },
  { value: 'expansion', label: 'Expansion' },
  { value: 'equipment', label: 'Equipment Purchase' },
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'fitout', label: 'Fit-out or New Location' },
  { value: 'new_business', label: 'New Business Set Up' }
];

const CHECKLIST_GROUPS = {
  A: ['Financials', 'Tax & Compliance', 'Banking', 'Business Plan', 'Security'],
  B: ['Financials', 'Tax & Compliance', 'Banking', 'Security'],
  C: ['Financials', 'Tax & Compliance', 'Banking', 'Forecasting', 'Governance', 'Security']
};

export default function AssessmentPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const assessment = useAssessment();
  
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Flatten questions for wizard
  const flatQuestions = questions ? Object.values(questions).flat() : [];
  const totalSteps = 3; // Business Profile, Funding Profile, Questions (Checklist moved to results)

  // Calculate progress
  const getProgress = () => {
    if (step === 0) return 15;
    if (step === 1) return 35;
    if (step === 2) {
      const questionProgress = flatQuestions.length > 0 
        ? (currentQuestionIndex / flatQuestions.length) * 60 
        : 0;
      return 40 + questionProgress;
    }
    return 100;
  };

  // Fetch questions and checklist when path is determined
  useEffect(() => {
    if (assessment.path) {
      fetchQuestionsAndChecklist();
    }
  }, [assessment.path]);

  const fetchQuestionsAndChecklist = async () => {
    try {
      const [questionsRes, checklistRes] = await Promise.all([
        assessmentApi.getQuestions(assessment.path),
        assessmentApi.getChecklist(assessment.path)
      ]);
      setQuestions(questionsRes.data.questions);
      setChecklist(checklistRes.data.checklist);
    } catch (error) {
      console.error('Failed to fetch assessment data:', error);
      toast.error('Failed to load assessment questions');
    }
  };

  const handleBusinessStatusSelect = async (status) => {
    assessment.setBusinessProfile({ business_status: status });
  };

  const handleLoanAmountSelect = async (amount) => {
    assessment.setLoanProfile({ loan_amount_band: amount });
    
    // Calculate path
    try {
      const response = await assessmentApi.calculatePath({
        business_status: assessment.businessProfile.business_status,
        loan_amount_band: amount
      });
      assessment.setPath(response.data.path);
    } catch (error) {
      console.error('Failed to calculate path:', error);
    }
  };

  const handleAnswerSelect = (questionId, answer, score) => {
    assessment.setResponse(questionId, answer, score);
  };

  const handleNext = () => {
    if (step === 0) {
      if (!assessment.businessProfile.business_name || !assessment.businessProfile.business_status) {
        toast.error('Please complete all fields');
        return;
      }
      setStep(1);
    } else if (step === 1) {
      if (!assessment.loanProfile.loan_amount_band || (assessment.loanProfile.funding_purposes || []).length === 0) {
        toast.error('Please complete all fields');
        return;
      }
      setStep(2); // Go directly to questions (skip checklist)
      setCurrentQuestionIndex(0);
    } else if (step === 2) {
      if (currentQuestionIndex < flatQuestions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        submitAssessment();
      }
    }
  };

  const handleBack = () => {
    if (step === 2 && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    try {
      const response = await assessmentApi.createAssessment({
        business_profile: assessment.businessProfile,
        loan_profile: assessment.loanProfile,
        responses: assessment.getResponsesList()
      });
      
      toast.success('Assessment completed!');
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 0) {
      return assessment.businessProfile.business_name && assessment.businessProfile.business_status;
    }
    if (step === 1) {
      return assessment.loanProfile.loan_amount_band && (assessment.loanProfile.funding_purposes || []).length > 0;
    }
    if (step === 2) {
      const currentQuestion = flatQuestions[currentQuestionIndex];
      return currentQuestion && assessment.responses[currentQuestion.id];
    }
    return false;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {['Profile', 'Funding', 'Questions'].map((label, index) => (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div className={`wizard-step-number ${
              step > index ? 'completed' : step === index ? 'active' : 'pending'
            }`}>
              {step > index ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${
              step >= index ? 'text-slate-900' : 'text-slate-400'
            }`}>
              {label}
            </span>
          </div>
          {index < 2 && (
            <div className={`w-8 h-0.5 ${step > index ? 'bg-sky-500' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderBusinessProfile = () => (
    <motion.div
      key="business-profile"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-heading mb-2">
          Client Business Details
        </h2>
        <p className="text-slate-600">
          Enter client business information
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="businessName" className="text-base">Client Business Name</Label>
          <Input
            id="businessName"
            placeholder="Enter client business name"
            className="h-12"
            value={assessment.businessProfile.business_name}
            onChange={(e) => assessment.setBusinessProfile({ business_name: e.target.value })}
            data-testid="business-name-input"
          />
        </div>

        <div className="space-y-3">
          <Label className="text-base">Business Status</Label>
          <div className="grid gap-4">
            <div
              className={`card-interactive p-5 flex items-center gap-4 ${
                assessment.businessProfile.business_status === 'existing' ? 'border-sky-500 bg-sky-50' : ''
              }`}
              onClick={() => handleBusinessStatusSelect('existing')}
              data-testid="status-existing"
            >
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Existing Business</div>
                <div className="text-sm text-slate-500">Already operating and generating revenue</div>
              </div>
              {assessment.businessProfile.business_status === 'existing' && (
                <CheckCircle2 className="w-6 h-6 text-sky-500 ml-auto" />
              )}
            </div>

            <div
              className={`card-interactive p-5 flex items-center gap-4 ${
                assessment.businessProfile.business_status === 'startup' ? 'border-sky-500 bg-sky-50' : ''
              }`}
              onClick={() => handleBusinessStatusSelect('startup')}
              data-testid="status-startup"
            >
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Rocket className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Start-up</div>
                <div className="text-sm text-slate-500">Not yet operating or pre-revenue</div>
              </div>
              {assessment.businessProfile.business_status === 'startup' && (
                <CheckCircle2 className="w-6 h-6 text-sky-500 ml-auto" />
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderLoanProfile = () => (
    <motion.div
      key="loan-profile"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-heading mb-2">
          Funding Requirements
        </h2>
        <p className="text-slate-600">
          Define the funding needs
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <Label className="text-base">Funding Amount Range</Label>
          <div className="grid gap-3">
            {LOAN_AMOUNTS.map((amount) => (
              <div
                key={amount.value}
                className={`card-interactive p-4 flex items-center gap-4 ${
                  assessment.loanProfile.loan_amount_band === amount.value ? 'border-sky-500 bg-sky-50' : ''
                }`}
                onClick={() => handleLoanAmountSelect(amount.value)}
                data-testid={`loan-${amount.value}`}
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-xl">
                  {amount.icon}
                </div>
                <span className="font-medium text-slate-900">{amount.label}</span>
                {assessment.loanProfile.loan_amount_band === amount.value && (
                  <CheckCircle2 className="w-5 h-5 text-sky-500 ml-auto" />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-base">Funding Purpose <span className="text-slate-500 font-normal">(select all that apply)</span></Label>
          <div className="grid grid-cols-2 gap-3">
            {FUNDING_PURPOSES.map((purpose) => {
              const isSelected = (assessment.loanProfile.funding_purposes || []).includes(purpose.value);
              return (
                <div
                  key={purpose.value}
                  className={`chip text-center flex items-center justify-center gap-2 ${isSelected ? 'selected' : ''}`}
                  onClick={() => assessment.toggleFundingPurpose(purpose.value)}
                  data-testid={`purpose-${purpose.value}`}
                >
                  {isSelected && <CheckCircle2 className="w-4 h-4" />}
                  {purpose.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderChecklistPreview = () => {
    const groups = checklist ? [...new Set(checklist.map(item => item.group))] : [];
    
    return (
      <motion.div
        key="checklist-preview"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-100 mb-4">
            <ClipboardList className="w-8 h-8 text-sky-600" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-heading mb-2">
            Documents Required
          </h2>
          <p className="text-slate-600">
            Based on the profile, here's what lenders typically require
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {groups.map((group) => {
            const groupItems = checklist.filter(item => item.group === group);
            return (
              <div key={group} className="card-default p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-sky-500" />
                  {group}
                </h3>
                <ul className="space-y-2">
                  {groupItems.map((item) => (
                    <li key={item.id} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">
                        {item.item}
                        {item.required && <span className="text-red-500 ml-1">*</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <span className="text-red-500">*</span> Required documents
        </p>
      </motion.div>
    );
  };

  const renderQuestions = () => {
    if (!flatQuestions.length) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      );
    }

    const question = flatQuestions[currentQuestionIndex];
    const selectedAnswer = assessment.responses[question.id]?.answer;

    return (
      <motion.div
        key={`question-${currentQuestionIndex}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-xl mx-auto"
      >
        <div className="mb-6">
          <span className="text-sm font-medium text-sky-600 uppercase tracking-wider">
            {question.section}
          </span>
          <div className="text-sm text-slate-500 mt-1">
            Question {currentQuestionIndex + 1} of {flatQuestions.length}
          </div>
        </div>

        <div className="question-card">
          <h2 className="question-title" data-testid="question-title">
            {question.question}
          </h2>
          <p className="question-helper">
            {question.helper}
          </p>

          <div className="space-y-3">
            {question.options.map((option) => (
              <div
                key={option.value}
                className={`answer-option ${selectedAnswer === option.value ? 'selected' : ''}`}
                onClick={() => handleAnswerSelect(question.id, option.value, option.score)}
                data-testid={`option-${option.value}`}
              >
                <div className="radio" />
                <span className="font-medium text-slate-700">{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-8 md:py-12" data-testid="assessment-page">
        <div className="container-main">
          {/* Progress Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <Progress value={getProgress()} className="h-2" />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>Start</span>
              <span>{Math.round(getProgress())}% Complete</span>
            </div>
          </div>

          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Content */}
          <AnimatePresence mode="wait">
            {step === 0 && renderBusinessProfile()}
            {step === 1 && renderLoanProfile()}
            {step === 2 && renderQuestions()}
          </AnimatePresence>

          {/* Navigation */}
          <div className="max-w-xl mx-auto mt-8">
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={step === 0 && currentQuestionIndex === 0}
                className="flex items-center gap-2"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>

              <Button
                onClick={handleNext}
                disabled={!canProceed() || submitting}
                className="btn-primary flex items-center gap-2"
                data-testid="next-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : step === 2 && currentQuestionIndex === flatQuestions.length - 1 ? (
                  <>
                    Complete Assessment
                    <CheckCircle2 className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
